import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { absoluteImageUrl } from "@/components/ImageUploader";
import { ArrowLeft, ImagePlus, Loader2, Trash2, ExternalLink, Plus, Check } from "lucide-react";

export default function AdminGalleryPage() {
  const [items, setItems] = useState([]);
  const [postImages, setPostImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [addingUrl, setAddingUrl] = useState(null);
  const inputRef = useRef(null);

  const load = () => api.get("/gallery").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/admin/gallery/post-images").then((r) => setPostImages(r.data)).catch(() => {});
  }, []);

  const addFromPost = async (img) => {
    setAddingUrl(img.url);
    try {
      await api.post("/admin/gallery", { url: img.url, caption: img.alt || img.title });
      toast.success("Poza a intrat în album — cititorii vor ajunge la însemnare dintr-un click.");
      load();
    } catch (err) {
      toast.error(formatApiError(err, "Nu am putut adăuga poza"));
    } finally {
      setAddingUrl(null);
    }
  };

  const addPhotos = async (e) => {
    const files = [...(e.target.files || [])];
    e.target.value = "";
    if (!files.length) return;
    setUploading(true);
    for (const file of files) {
      if (!file.type.startsWith("image/")) continue;
      try {
        const form = new FormData();
        form.append("file", file);
        const { data } = await api.post("/admin/upload", form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        let caption = "";
        try {
          const res = await api.post("/admin/describe-image", { url: data.url });
          caption = res.data?.alt || "";
        } catch {
          // fără propunere AI
        }
        await api.post("/admin/gallery", { url: data.url, caption });
      } catch (err) {
        toast.error(formatApiError(err, "Nu am putut adăuga o poză"));
      }
    }
    setUploading(false);
    toast.success("Pozele au fost adăugate în album — descrierile sunt propuse de AI, le poți edita.");
    load();
  };

  const saveCaption = async (item, caption) => {
    if (caption === item.caption) return;
    try {
      await api.put(`/admin/gallery/${item.id}`, { caption });
      setItems((prev) => prev.map((x) => (x.id === item.id ? { ...x, caption } : x)));
      toast.success("Descrierea a fost salvată.");
    } catch (err) {
      toast.error(formatApiError(err, "Nu am putut salva descrierea"));
    }
  };

  const remove = async (item) => {
    try {
      await api.delete(`/admin/gallery/${item.id}`);
      setItems((prev) => prev.filter((x) => x.id !== item.id));
      toast.success("Poza a fost scoasă din album.");
    } catch (err) {
      toast.error(formatApiError(err, "Nu am putut șterge poza"));
    }
  };

  return (
    <main className="mx-auto max-w-5xl px-6 pb-24 pt-12">
      <Link to="/admin" data-testid="gallery-back-link" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#F7F1E8]/78 hover:text-[#F7F1E8]">
        <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.6} /> Înapoi la Atelier
      </Link>
      <div className="mt-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl font-medium text-[var(--milk)]">Galeria blogului</h1>
          <p className="mt-2 max-w-lg text-sm text-[#F7F1E8]/78">
            Albumul blogului — alegi pozele preferate, iar cititorii ajung la însemnarea fiecăreia dintr-un click, pe pagina publică{" "}
            <a href="/galerie" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[var(--accent)] underline-offset-2 hover:underline">
              /galerie <ExternalLink className="h-3 w-3" strokeWidth={1.6} />
            </a>
            .
          </p>
        </div>
        <button
          type="button"
          data-testid="gallery-add-btn"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="inline-flex items-center gap-2 rounded-full bg-[var(--coral)] px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] text-white shadow-sm transition-colors hover:bg-[#7E0C13] disabled:opacity-60"
        >
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.8} /> : <ImagePlus className="h-4 w-4" strokeWidth={1.8} />}
          {uploading ? "Se încarcă…" : "Adaugă poze"}
        </button>
        <input ref={inputRef} type="file" accept="image/*" multiple className="hidden" data-testid="gallery-file-input" onChange={addPhotos} />
      </div>

      {(() => {
        const inAlbum = new Set(items.map((x) => x.url));
        const candidates = postImages.filter((img) => !inAlbum.has(img.url));
        if (!candidates.length) return null;
        return (
          <section className="mt-12 rounded-3xl bg-[var(--tint-mint)] p-6 sm:p-8" data-testid="admin-gallery-picker">
            <h2 className="font-serif text-xl font-medium text-[var(--milk)]">Alege din însemnări</h2>
            <p className="mt-1 text-sm text-[#F7F1E8]/78">
              Ilustrațiile din însemnările publicate — apasă „+" ca să le pui în album. Așa, cititorul ajunge automat la însemnare când dă click pe poză.
            </p>
            <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
              {candidates.map((img, i) => (
                <div key={img.url} className="rounded-2xl bg-[#2B1B13] p-2 shadow-sm" data-testid={`picker-item-${i}`}>
                  <div className="relative">
                    <img src={absoluteImageUrl(img.url)} alt={img.alt || img.title} loading="lazy" className="aspect-square w-full rounded-xl object-cover" />
                    <button
                      type="button"
                      data-testid={`picker-add-${i}`}
                      onClick={() => addFromPost(img)}
                      disabled={addingUrl === img.url}
                      aria-label="Adaugă în album"
                      className="absolute right-2 top-2 rounded-full bg-[var(--accent)] p-2 text-white shadow-md transition-transform hover:scale-110 active:scale-95 disabled:opacity-60"
                    >
                      {addingUrl === img.url ? <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.8} /> : <Plus className="h-4 w-4" strokeWidth={2} />}
                    </button>
                  </div>
                  <p className="mt-2 truncate px-1 text-[11px] text-[#F7F1E8]/78" title={img.title}>{img.title}</p>
                </div>
              ))}
            </div>
          </section>
        );
      })()}

      {items.length === 0 && !uploading && (
        <div className="mt-12 rounded-3xl border-2 border-dashed border-[color-mix(in_srgb,var(--milk)_15%,transparent)] p-14 text-center text-sm text-[#F7F1E8]/68" data-testid="admin-gallery-empty">
          Albumul e gol deocamdată — apasă „Adaugă poze" și alege oricâte odată.
        </div>
      )}

      <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((item) => (
          <div key={item.id} data-testid={`admin-gallery-item-${item.id}`} className="rounded-2xl bg-[#2B1B13] p-3 shadow-sm ring-1 ring-[color-mix(in_srgb,var(--milk)_14%,transparent)]">
            <div className="relative">
              <img src={absoluteImageUrl(item.url)} alt={item.caption || "Amintire"} className="aspect-[4/3] w-full rounded-xl object-cover" />
              <button
                type="button"
                data-testid={`admin-gallery-delete-${item.id}`}
                onClick={() => remove(item)}
                className="absolute right-2 top-2 rounded-full bg-[#2B1B13]/95 p-2 text-red-500 shadow-sm ring-1 ring-red-100 hover:bg-[#2B1B13]"
                aria-label="Șterge poza"
              >
                <Trash2 className="h-4 w-4" strokeWidth={1.6} />
              </button>
            </div>
            <input
              defaultValue={item.caption}
              maxLength={300}
              placeholder="O descriere scurtă, caldă…"
              data-testid={`admin-gallery-caption-${item.id}`}
              onBlur={(e) => saveCaption(item, e.target.value.trim())}
              className="mt-3 w-full rounded-xl border border-[color-mix(in_srgb,var(--milk)_18%,transparent)] bg-[#2B1B13] px-3 py-2 text-xs focus:border-[var(--accent)] focus:outline-none focus:ring-2 focus:ring-[var(--tint-pink)]"
            />
            <p className="mt-2 flex items-center gap-1.5 px-1 text-[11px]" data-testid={`admin-gallery-link-status-${item.id}`}>
              {item.slug ? (
                <>
                  <Check className="h-3 w-3 text-emerald-600" strokeWidth={2} />
                  <span className="truncate text-emerald-700">Duce la: {item.post_title}</span>
                </>
              ) : (
                <span className="text-[#F7F1E8]/58">Nu e legată de nicio însemnare — se deschide mărită.</span>
              )}
            </p>
          </div>
        ))}
      </div>
    </main>
  );
}
